import './ViewTemplateTable.scss';
import { useExcelData } from "components/Contexts/ExcelDataContext";
import { ExcelKeyValuePairs } from "components/Contexts/ExcelKeyValuePairs";
import { useCallback, useState, useEffect } from "react";
import { ExcelViewerErrorData } from "types";
import { stripCaseAndSpaces } from "utils";
import { ExcelTemplateTypes, 
         FloatItemTemplateApiResponseKeys, 
         FloatProductLineTemplateApiResponseKeys,
         SupplierTemplateApiResponseKeys,
         SpecialFixedTemplateApiResponseKeys, 
         SpecialPricingExpirationTemplateApiResponseKeys, 
         PromotionExpirationTemplateApiResponseKeys,
         PromotionEntryTemplateApiResponseKeys} from 'enums';
import Cell from '../TemplateOptions/Cell';

import { VariableSizeGrid as Grid } from 'react-window';

interface Props {
    templateType: ExcelTemplateTypes
    numHeaders?: number
    gridRef?: React.RefObject<Grid<any>>
}

interface CellProps {
    kvps: ExcelKeyValuePairs, 
    key: string, 
    rowNumber: number, 
    matchingErrors: ExcelViewerErrorData[] | null | undefined,
    isHeader: boolean,
    isRowNumber: boolean,
    editingCell: { row: number; col: string } | null,
    onDeleteRow: (rowIndex: number) => void,
    rowHasError: boolean
}

//Maps keys in API response errors to the column keys of the uploaded excel template.
//The comma is not a typo, required here to distinguish between a TypeScript generic and a JSX element.
const mapColumns = <TApiResponseKeys extends object,>(keyEnum: TApiResponseKeys, apiErrorKeys: string[]) => {
    const columns: string[] = [];
    for (const apiErrorKey of apiErrorKeys) {
        const validKey = apiErrorKey in keyEnum,
              column = validKey ? `${keyEnum[apiErrorKey as keyof typeof keyEnum]}`
                                : apiErrorKey;
        columns.push(column);
    }
    return columns;
}


export const ViewTemplateTable = ({ templateType, numHeaders = 0, gridRef }: Props) => {
    const { excelContext, errorContext } = useExcelData(),
          { excelData, setExcelData } = excelContext,
          { excelErrors, setExcelErrors } = errorContext,
            errorList = excelErrors as ExcelViewerErrorData[],
            rowNumberKey = "RowNumber";

    // State to keep track of current editable data that will be revised
    const [editableData, setEditableData] = useState(excelData);

    // Function that handles updates to the excel template
    const [editingCell, setEditingCell] = useState<{ row: number; col: string } | null>(null);

    const handleInputChange = useCallback((rowIndex: number, key: string, value: string) => {
        setEditingCell({ row: rowIndex, col: key }); // track the editing cell
        setEditableData((prevData: any) => {
            const newData = [...prevData];
            newData[rowIndex][key] = value;
            return newData;
        });

    }, [setEditableData]);

    //Reverse-mapping API response error keys to template columns
    const mapKeysToViewerColumns = useCallback((compositeKey: string[] | null | undefined): string[] => {
        if (!compositeKey || compositeKey.length === 0) return [""];
        else {
            const apiErrorKeys = compositeKey?.map(x => stripCaseAndSpaces(x)) ?? [];
            switch (templateType) {
                case ExcelTemplateTypes.Supplier:
                    return mapColumns(SupplierTemplateApiResponseKeys, apiErrorKeys);
                case ExcelTemplateTypes.FloatItem:
                    if (apiErrorKeys.includes("EMPLOYEENUMBER")) apiErrorKeys.unshift("EMPLYEENUMBER");
                    return mapColumns(FloatItemTemplateApiResponseKeys, apiErrorKeys);
                case ExcelTemplateTypes.FloatProductLine:
                    return mapColumns(FloatProductLineTemplateApiResponseKeys, apiErrorKeys);
                case ExcelTemplateTypes.Fixed:
                    if (apiErrorKeys.includes("EMPLOYEENUMBER")) apiErrorKeys.unshift("EMPLYEENUMBER");
                    return mapColumns(SpecialFixedTemplateApiResponseKeys, apiErrorKeys);
                case ExcelTemplateTypes.SpecialPricingExpiration:
                    return mapColumns(SpecialPricingExpirationTemplateApiResponseKeys, apiErrorKeys);
                case ExcelTemplateTypes.PromotionExpiration:
                    return mapColumns(PromotionExpirationTemplateApiResponseKeys, apiErrorKeys);
                case ExcelTemplateTypes.PromotionEntry:
                    return mapColumns(PromotionEntryTemplateApiResponseKeys, apiErrorKeys);
                default:
                    return apiErrorKeys;
            }
        }
    }, [templateType]);


    //Populates row data, highlights individual cells if both row and column are provided in the error list
    const generateDataCell = useCallback((cellProps: CellProps) => {
        const { key, rowNumber, matchingErrors, isHeader, isRowNumber, editingCell, onDeleteRow, rowHasError } = cellProps,
            column = stripCaseAndSpaces(key),
            highlighted: boolean = (matchingErrors != null && matchingErrors.length > 0),
            errorDetails = matchingErrors?.filter((x) => mapKeysToViewerColumns(x.column.split(',')).includes(column)) ?? [],
            hasCellError: boolean = (errorDetails != null && errorDetails.length > 0),
            emphasizeThisCell: boolean = (highlighted && hasCellError),
            mouseoverText = hasCellError ? errorDetails.map(x => x.info.join("\n")).join("\n") : "";

        let value: string;
        if (key !== rowNumberKey) {
            value = editableData[rowNumber - 2][key]?.toString() || "";
            //console.log("rowNumber: " + rowNumber + " | key: " + key + " | value : " + value);
        } else {
            value = rowNumber - 1 > numHeaders ? rowNumber.toString() : "";
        }

        return (
            <Cell
                key={`${rowNumber - 2}-${key}`}
                value={value}
                rowIndex={rowNumber - 2}
                columnKey={key}
                onChange={handleInputChange}
                isHeader={isHeader}
                isRowNumber={key === rowNumberKey}
                hasError={emphasizeThisCell}
                rowHasError={rowHasError}
                mouseoverText={mouseoverText}
                isEditing={editingCell?.row === rowNumber - 2 && editingCell.col === key}
                onDeleteRow={onDeleteRow}
            />
        );
    }, [numHeaders, mapKeysToViewerColumns, editableData, handleInputChange]); 

    const [gridWidth, setGridWidth] = useState(() => window.innerWidth * 0.95);

    useEffect(() => {
        const handleResize = () => {
            setGridWidth(window.innerWidth * 0.95);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;

            // If the user clicked outside the table or any grid input
            if (!target.closest('.ReactVirtualized__Grid') && !target.closest('input')) {
                setEditingCell(null); 
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const [deletingRowIndex, setDeletingRowIndex] = useState<number | null>(null);

    const handleDeleteRow = useCallback((rowIndex: number) => {
        setDeletingRowIndex(rowIndex); // Trigger animation

        setTimeout(() => {
            setEditableData((prevData: any[]) => {
                const newData = [...prevData];
                newData.splice(rowIndex, 1);
                setExcelData(newData);

                // Handle errors
                setExcelErrors((prevErrors: ExcelViewerErrorData[] | null) => {
                    if (!prevErrors) return null;
                    return prevErrors
                        .filter(err => err.row !== rowIndex + 2)
                        .map(err => err.row > rowIndex + 2 ? { ...err, row: err.row - 1 } : err);
                });

                return newData;
            });

            setDeletingRowIndex(null); // Reset
        }, 300); 
    }, [setEditableData, setExcelData, setExcelErrors]);


    return (
        <Grid
            style={{
                margin: '0 auto',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'lightgray',
                borderRadius: '10px',
            }}
            columnCount={(editableData[0] ? Object.keys(editableData[0]).length : 0) + 1}// +1 for RowNumber column
            columnWidth={(index) => index === 0 ? 70 : 200}
            height={600}
            rowCount={editableData.length + 1}
            rowHeight={() => 45}
            width={gridWidth}
            ref={gridRef}
        >
            {({ columnIndex, rowIndex, style }) => {

                if (rowIndex === 0) {
                    // HEADER row
                    const key = columnIndex === 0
                        ? "ROW#"
                        : Object.keys(excelData[0])[columnIndex - 1];

                    const displayKey = key.replace(/^(.+)_\d+$/, "$1");

                    return (
                        <div style={style} className="headerCell">
                            {displayKey}
                        </div>
                    );
                }

                // DATA row
                const rowNumber = rowIndex + 1; // because 0 is header
                const rowData = editableData[rowIndex - 1]; // shift down by 1
                const key = columnIndex === 0
                    ? rowNumberKey
                    : Object.keys(rowData)[columnIndex - 1];

                const matchingErrors = errorList?.filter(
                    (err: ExcelViewerErrorData) => err.row === rowNumber
                ) ?? [];

                const props: CellProps = {
                    kvps: rowData,
                    key,
                    rowNumber,
                    matchingErrors,
                    isHeader: rowIndex - 1 < numHeaders,
                    isRowNumber: key === rowNumberKey,
                    editingCell: editingCell,
                    onDeleteRow: handleDeleteRow,
                    rowHasError: matchingErrors.length > 0
                };

                return (
                    <div
                        style={style}
                        key={`${rowIndex}-${columnIndex}`}
                        className={`cell ${deletingRowIndex === rowIndex - 1 ? 'deleting-row' : ''}`}>
                        {generateDataCell(props)}
                    </div>
                );
            }}
        </Grid>
    );
}
